<template>
  <div class="mods-map" :class="{ 'mods-map--loading': loading }">
    <div v-if="loading" class="mods-map__loading">
      <v-progress-circular
        indeterminate
        color="primary"
        size="42"
        width="4"
        class="mb-3"
      />
      <div class="mods-map__loading-title">Загрузка домофонов на карту…</div>
      <div v-if="loadProgress.total" class="mods-map__loading-progress">
        {{ loadProgress.loaded }} / {{ loadProgress.total }}
      </div>
      <v-progress-linear
        v-if="loadProgress.total"
        :value="loadPercent"
        height="6"
        rounded
        color="primary"
        class="mods-map__loading-bar mt-3"
      />
    </div>

    <div class="mods-map__toolbar">
      <div class="mods-map__tools">
        <v-btn
          small
          rounded
          class="text-none mr-2"
          :color="drawMode ? 'primary' : undefined"
          :outlined="!drawMode"
          :dark="drawMode"
          :disabled="loading"
          @click="toggleDraw"
        >
          <v-icon left small>mdi-selection-drag</v-icon>
          Выделить область
        </v-btn>
        <v-btn
          small
          rounded
          outlined
          class="text-none"
          color="primary"
          :disabled="!bboxRect || loading"
          @click="clearBbox"
        >
          <v-icon left small>mdi-selection-off</v-icon>
          Сбросить область
        </v-btn>
      </div>
      <div class="mods-map__legend">
        <span class="mods-map__legend-item">
          <span class="mods-map__dot mods-map__dot--region" /> Район
        </span>
        <span class="mods-map__legend-item">
          <span class="mods-map__dot mods-map__dot--marker" /> Домофон
        </span>
        <span class="mods-map__legend-item">
          <span class="mods-map__dot mods-map__dot--selected" /> Выбран
        </span>
      </div>
    </div>

    <v-alert
      v-if="drawMode"
      dense
      colored-border
      border="left"
      color="primary"
      class="mb-2"
    >
      <v-icon small color="primary" class="mr-1">mdi-cursor-default-click</v-icon>
      Зажмите ЛКМ и обведите квартал на карте
    </v-alert>

    <l-map
      ref="map"
      class="mods-map__canvas"
      :center="mapCenter"
      :zoom="11"
      :options="{ zoomControl: false, attributionControl: false }"
      @ready="onMapReady"
    >
      <l-control-zoom
        position="topleft"
        zoom-in-text="<i style='color: black;'>+</i>"
        zoom-out-text="<i style='color: black;'>-</i>"
      />
      <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

      <l-polygon
        v-for="region in regionPolygons"
        :key="'r-' + region.id"
        :lat-lngs="region.polygon"
        :options="regionPolygonOptions(region.id)"
        @click="onRegionClick(region.id)"
      >
        <l-tooltip :options="{ sticky: true }">
          {{ region.name }} — нажмите, чтобы выбрать район
        </l-tooltip>
      </l-polygon>

      <l-rectangle
        v-if="bboxRect"
        :bounds="bboxRect"
        :l-style="bboxOptions"
      />
    </l-map>

    <v-alert
      v-if="!loading && !mapMarkers.length"
      type="info"
      dense
      outlined
      class="ma-2"
    >
      <v-icon small class="mr-1">mdi-map-marker-off</v-icon>
      Нет домофонов с координатами по текущему фильтру. Используйте вкладку «Список».
    </v-alert>
  </div>
</template>

<script>
import L, { divIcon } from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { LMap, LControlZoom, LTileLayer, LPolygon, LTooltip, LRectangle } from 'vue2-leaflet';
import { REGION_TERRITORIES } from '@/components/areas/regionTerritories';

const CHUNK_SIZE = 400;

export default {
  name: 'MassOpenDoorSoundMap',
  components: {
    LMap,
    LTileLayer,
    LPolygon,
    LTooltip,
    LControlZoom,
    LRectangle,
  },
  props: {
    markers: {
      type: Array,
      default: () => [],
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    regions: {
      type: Array,
      default: () => [],
    },
    selectedRegionIds: {
      type: Array,
      default: () => [],
    },
    bbox: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadProgress: {
      type: Object,
      default: () => ({ loaded: 0, total: 0 }),
    },
  },
  data () {
    return {
      drawMode: false,
      drawing: false,
      startLatLng: null,
      bboxRect: null,
      mapObject: null,
      clusterGroup: null,
      leafletMarkersById: {},
      renderToken: 0,
      bboxOptions: {
        color: '#1565c0',
        weight: 2,
        fillColor: '#42a5f5',
        fillOpacity: 0.2,
      },
    };
  },
  computed: {
    mapCenter () {
      if (this.mapMarkers.length) {
        const first = this.mapMarkers[0];
        return [first.geo_lat, first.geo_lon];
      }
      return [54.314, 48.403];
    },
    mapMarkers () {
      return (this.markers || []).filter(m => m.geo_lat != null && m.geo_lon != null);
    },
    regionPolygons () {
      return (this.regions || [])
        .map((region) => {
          const data = REGION_TERRITORIES[region.id];
          if (!data || !data.polygon) return null;
          return {
            id: region.id,
            name: region.name,
            polygon: data.polygon,
          };
        })
        .filter(Boolean);
    },
    selectedIdSet () {
      return new Set(this.selectedIds);
    },
    loadPercent () {
      if (!this.loadProgress.total) return 0;
      return Math.min(100, Math.round((this.loadProgress.loaded / this.loadProgress.total) * 100));
    },
  },
  watch: {
    bbox: {
      immediate: true,
      handler (val) {
        if (!val) {
          this.bboxRect = null;
          return;
        }
        const parts = val.split(',').map(Number);
        if (parts.length === 4) {
          this.bboxRect = [
            [parts[0], parts[1]],
            [parts[2], parts[3]],
          ];
        }
      },
    },
    mapMarkers () {
      this.rebuildMarkers();
    },
    selectedIds () {
      this.syncMarkerSelection();
    },
    loading (value) {
      if (!value) {
        this.$nextTick(() => this.invalidateMapSize());
      }
    },
  },
  mounted () {
    this.$nextTick(() => {
      this.invalidateMapSize();
    });
  },
  beforeDestroy () {
    this.detachDrawHandlers();
    this.clearMarkers();
  },
  methods: {
    regionPolygonOptions (regionId) {
      const selected = this.selectedRegionIds.includes(regionId);
      return {
        color: selected ? '#c62828' : '#d32f2f',
        weight: selected ? 3 : 2,
        fillColor: selected ? '#ef9a9a' : '#f8bbd0',
        fillOpacity: selected ? 0.55 : 0.25,
      };
    },
    onRegionClick (regionId) {
      this.$emit('region-click', regionId);
    },
    onMapReady () {
      const map = this.$refs.map && this.$refs.map.mapObject;
      if (!map) return;
      this.mapObject = map;
      this.ensureClusterGroup();
      this.rebuildMarkers();
      this.invalidateMapSize();
    },
    ensureClusterGroup () {
      if (!this.mapObject || this.clusterGroup) return;

      this.clusterGroup = L.markerClusterGroup({
        showCoverageOnHover: false,
        removeOutsideVisibleBounds: true,
        zoomToBoundsOnClick: false,
        maxClusterRadius: 50,
        spiderLegPolylineOptions: {
          color: 'transparent',
        },
        iconCreateFunction: this.customIconCreateFunction,
      });

      this.clusterGroup.on('clusterclick', (event) => {
        if (this.mapObject) {
          this.mapObject.fitBounds(event.layer.getBounds().pad(0.5));
        }
      });

      this.mapObject.addLayer(this.clusterGroup);
    },
    clearMarkers () {
      this.renderToken += 1;
      if (this.clusterGroup) {
        this.clusterGroup.clearLayers();
      }
      this.leafletMarkersById = {};
    },
    rebuildMarkers () {
      if (!this.mapObject) return;
      this.ensureClusterGroup();
      this.clearMarkers();

      const token = this.renderToken;
      const items = this.mapMarkers;
      let index = 0;

      const addChunk = () => {
        if (token !== this.renderToken || !this.clusterGroup) return;

        const chunk = [];
        const end = Math.min(index + CHUNK_SIZE, items.length);
        for (; index < end; index += 1) {
          const markerData = items[index];
          const leafletMarker = this.createLeafletMarker(markerData);
          this.leafletMarkersById[markerData.id] = leafletMarker;
          chunk.push(leafletMarker);
        }

        if (chunk.length) {
          this.clusterGroup.addLayers(chunk);
        }

        if (index < items.length) {
          window.requestAnimationFrame(addChunk);
        } else {
          this.invalidateMapSize();
        }
      };

      window.requestAnimationFrame(addChunk);
    },
    createLeafletMarker (markerData) {
      const selected = this.selectedIdSet.has(markerData.id);
      const leafletMarker = L.marker([markerData.geo_lat, markerData.geo_lon], {
        icon: this.buildMarkerIcon(selected),
      });

      leafletMarker.bindTooltip(this.buildTooltipHtml(markerData, selected), {
        sticky: true,
        direction: 'top',
      });

      leafletMarker.on('click', () => {
        this.$emit('marker-click', markerData);
      });

      leafletMarker.__massSelected = selected;
      leafletMarker.__massData = markerData;
      return leafletMarker;
    },
    syncMarkerSelection () {
      const selectedSet = this.selectedIdSet;
      Object.keys(this.leafletMarkersById).forEach((id) => {
        const leafletMarker = this.leafletMarkersById[id];
        if (!leafletMarker) return;
        const selected = selectedSet.has(Number(id));
        if (leafletMarker.__massSelected === selected) return;

        leafletMarker.__massSelected = selected;
        leafletMarker.setIcon(this.buildMarkerIcon(selected));
        leafletMarker.setTooltipContent(
          this.buildTooltipHtml(leafletMarker.__massData, selected),
        );
      });
    },
    buildMarkerIcon (selected) {
      return divIcon({
        className: '',
        html: selected
          ? '<div class="marker marker--selected"><i class="mdi mdi-map-marker-check marker-icon--selected"></i></div>'
          : '<div class="marker"><i class="mdi mdi-map-marker action-icon"></i></div>',
        iconSize: [28, 28],
        iconAnchor: [14, 28],
      });
    },
    buildTooltipHtml (markerData, selected) {
      const address = this.escapeHtml(markerData.address || '');
      const model = this.escapeHtml(markerData.model || '');
      const hint = selected ? 'Выбран — нажмите, чтобы снять' : 'Нажмите, чтобы выбрать';
      return `<div><strong>${address}</strong></div><div>${model}</div><div class="caption">${hint}</div>`;
    },
    escapeHtml (value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    invalidateMapSize () {
      if (this.$refs.map && this.$refs.map.mapObject) {
        this.$refs.map.mapObject.invalidateSize();
      }
    },
    customIconCreateFunction (cluster) {
      const childCount = cluster.getChildCount();
      let size = 40;
      let className = 'custom-cluster-icon';
      if (childCount < 10) {
        size = 36;
        className += ' custom-cluster-icon-small';
      } else if (childCount < 100) {
        size = 44;
        className += ' custom-cluster-icon-medium';
      } else {
        size = 52;
        className += ' custom-cluster-icon-large';
      }
      return divIcon({
        html: `<div class="${className}"><span>${childCount}</span></div>`,
        className: 'mods-map-cluster-wrap',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    },
    toggleDraw () {
      this.drawMode = !this.drawMode;
      if (this.drawMode) {
        this.attachDrawHandlers();
      } else {
        this.detachDrawHandlers();
      }
    },
    attachDrawHandlers () {
      if (!this.mapObject) return;
      this.mapObject.dragging.disable();
      this.mapObject.on('mousedown', this.onDrawStart);
      this.mapObject.on('mousemove', this.onDrawMove);
      this.mapObject.on('mouseup', this.onDrawEnd);
    },
    detachDrawHandlers () {
      if (!this.mapObject) return;
      this.mapObject.dragging.enable();
      this.mapObject.off('mousedown', this.onDrawStart);
      this.mapObject.off('mousemove', this.onDrawMove);
      this.mapObject.off('mouseup', this.onDrawEnd);
      this.drawing = false;
      this.startLatLng = null;
    },
    onDrawStart (e) {
      if (!this.drawMode) return;
      this.drawing = true;
      this.startLatLng = e.latlng;
      this.bboxRect = [
        [e.latlng.lat, e.latlng.lng],
        [e.latlng.lat, e.latlng.lng],
      ];
    },
    onDrawMove (e) {
      if (!this.drawing || !this.startLatLng) return;
      const south = Math.min(this.startLatLng.lat, e.latlng.lat);
      const north = Math.max(this.startLatLng.lat, e.latlng.lat);
      const west = Math.min(this.startLatLng.lng, e.latlng.lng);
      const east = Math.max(this.startLatLng.lng, e.latlng.lng);
      this.bboxRect = [
        [south, west],
        [north, east],
      ];
    },
    onDrawEnd () {
      if (!this.drawing || !this.bboxRect) return;
      this.drawing = false;
      const [[south, west], [north, east]] = this.bboxRect;
      const bbox = `${south},${west},${north},${east}`;
      this.$emit('bbox-select', bbox);
      this.drawMode = false;
      this.detachDrawHandlers();
    },
    clearBbox () {
      this.bboxRect = null;
      this.$emit('bbox-select', null);
    },
  },
};
</script>

<style scoped>
.mods-map {
  position: relative;
  z-index: 0;
  height: 100%;
  min-height: 520px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.mods-map__loading {
  position: absolute;
  inset: 0;
  z-index: 500;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(2px);
  text-align: center;
  padding: 24px;
}
.mods-map__loading-title {
  font-weight: 600;
  color: #37474f;
}
.mods-map__loading-progress {
  margin-top: 6px;
  font-size: 0.85rem;
  color: #607d8b;
  font-variant-numeric: tabular-nums;
}
.mods-map__loading-bar {
  width: min(280px, 70%);
}
.mods-map__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0 10px;
}
.mods-map__tools {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.mods-map__legend {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 0.75rem;
  color: #607d8b;
}
.mods-map__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.mods-map__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
.mods-map__dot--region {
  background: #ef9a9a;
  border: 1px solid #c62828;
}
.mods-map__dot--marker {
  background: #90caf9;
  border: 1px solid #1565c0;
}
.mods-map__dot--selected {
  background: #81c784;
  border: 1px solid #2e7d32;
}
.mods-map__canvas {
  flex: 1;
  min-height: 460px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e3eaf2;
}
</style>

<style>
.mods-map .marker {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mods-map .marker .mdi {
  font-size: 22px;
  line-height: 1;
}
.mods-map .action-icon {
  color: #1565c0;
}
.mods-map .marker-icon--selected {
  color: #2e7d32;
}
.mods-map .mods-map-cluster-wrap {
  background: transparent !important;
  border: none !important;
}
.mods-map .custom-cluster-icon {
  background: #1565c0;
  color: #fff;
  border-radius: 50%;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  border: 2px solid #fff;
  box-shadow: 0 2px 8px rgba(13, 64, 146, 0.35);
  box-sizing: border-box;
}
.mods-map .custom-cluster-icon-small {
  font-size: 12px;
}
.mods-map .custom-cluster-icon-medium {
  font-size: 13px;
}
.mods-map .custom-cluster-icon-large {
  font-size: 14px;
}
</style>
